import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Clock, Mail } from 'lucide-react';
import { ARTICLES, RUBRICS, BRAND } from '../data/mock';
import { Reveal, Magnetic, AnimatedDivider } from '../components/effects';

const rubricLabel = (slug) => RUBRICS.find((r) => r.slug === slug)?.label || '';
const rubricDesc  = (slug) => RUBRICS.find((r) => r.slug === slug)?.desc  || '';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });

/* Reading progress bar */
const ReadingProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      const pct = total > 0 ? (h.scrollTop / total) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, pct)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className="fixed top-0 inset-x-0 z-[60] h-[2px] bg-transparent pointer-events-none">
      <div
        className="h-full bg-[#C9A875] transition-[width] duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

/* Block renderers */
const Block = ({ block, isFirst }) => {
  if (block.type === 'h2') {
    return (
      <h2
        className="font-display text-[#1A1A1A] mt-16 mb-7 leading-[1.15] tracking-[-0.012em]"
        style={{
          fontSize: 'clamp(26px, 2.4vw, 34px)',
          fontWeight: 400,
          fontVariationSettings: '"opsz" 72, "SOFT" 30',
        }}
      >
        {block.text}
      </h2>
    );
  }
  if (block.type === 'pullquote') {
    return (
      <blockquote
        className="my-16 lg:my-20 text-center mx-auto max-w-[640px]"
      >
        <span
          className="block font-display italic text-[#1A1A1A] leading-[1.28] tracking-[-0.008em]"
          style={{
            fontSize: 'clamp(26px, 2.8vw, 38px)',
            fontWeight: 300,
            fontVariationSettings: '"opsz" 72, "SOFT" 30',
          }}
        >
          <span className="text-[#9A7F4F] not-italic font-medium mr-1.5">"</span>
          {block.text}
          <span className="text-[#9A7F4F] not-italic font-medium ml-1">"</span>
        </span>
        <span className="block mt-7 mx-auto h-px w-16 bg-[#9A7F4F]/55" />
      </blockquote>
    );
  }
  if (block.type === 'note') {
    return (
      <aside className="my-12 border-l-2 border-[#9A7F4F] pl-6 py-2">
        <div
          className="text-[10px] tracking-[0.32em] uppercase text-[#9A7F4F] font-medium mb-2"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          Editör Notu
        </div>
        <p className="text-[14.5px] text-[#1A1A1A]/72 leading-[1.7] italic font-display" style={{ fontSize: '17px', fontWeight: 400 }}>
          {block.text}
        </p>
      </aside>
    );
  }
  if (block.type === 'cta') {
    return (
      <aside className="my-14 lg:my-16 bg-[#72272D] text-[#F5F0E5] p-8 lg:p-10 rounded-sm relative overflow-hidden">
        <div className="absolute top-0 left-0 h-full w-1 bg-[#C9A875]" />
        <div className="relative">
          <div
            className="text-[10px] tracking-[0.32em] uppercase text-[#C9A875] font-medium mb-3"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Operasyonel Not
          </div>
          <p
            className="font-display italic leading-[1.32] tracking-[-0.008em]"
            style={{
              fontSize: 'clamp(20px, 2vw, 26px)',
              fontWeight: 300,
              fontVariationSettings: '"opsz" 72, "SOFT" 30',
            }}
          >
            {block.text}
          </p>
          {block.label && block.href && (
            <Link
              to={block.href}
              className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.20em] uppercase text-[#C9A875] border-b border-[#C9A875]/45 pb-1.5 hover:border-[#C9A875] transition-colors"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              {block.label}
              <ArrowRight size={13} />
            </Link>
          )}
        </div>
      </aside>
    );
  }
  // Default paragraph
  if (isFirst) {
    const [first, ...rest] = block.text;
    return (
      <p className="text-[#1A1A1A] text-[17px] lg:text-[18px] leading-[1.78] tracking-[0.005em]">
        <span
          className="font-display italic text-[#9A7F4F] float-left mr-3 mt-1"
          style={{
            fontSize: '78px',
            lineHeight: '0.85',
            fontWeight: 400,
            fontVariationSettings: '"opsz" 144, "SOFT" 30',
          }}
        >
          {first}
        </span>
        {rest.join('')}
      </p>
    );
  }
  return (
    <p className="mt-7 text-[#1A1A1A] text-[17px] lg:text-[18px] leading-[1.78] tracking-[0.005em]">
      {block.text}
    </p>
  );
};

/* Final CTA */
const EndCTA = () => (
  <section className="py-24 lg:py-32 bg-[#F5F3EF]">
    <div className="container-x max-w-3xl mx-auto text-center">
      <Reveal>
        <div className="inline-flex items-center justify-center gap-3 mb-9">
          <span className="h-px w-12 bg-[#9A7F4F]" />
          <span
            className="text-[10.5px] tracking-[0.35em] uppercase text-[#9A7F4F] font-medium"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Sonraki Adım
          </span>
          <span className="h-px w-12 bg-[#9A7F4F]" />
        </div>
      </Reveal>

      <Reveal delay={150}>
        <h2
          className="font-display text-[#1A1A1A] leading-[1.06] tracking-[-0.018em]"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 46px)',
            fontWeight: 400,
            fontVariationSettings: '"opsz" 96, "SOFT" 30',
          }}
        >
          Bu seviye disipline sahip bir
          <span className="italic font-light text-[#9A7F4F]"> yemek operasyonu </span>
          kurmamız ister misiniz?
        </h2>
      </Reveal>

      <Reveal delay={350}>
        <p className="mt-7 text-[15px] lg:text-[16.5px] text-[#1A1A1A]/68 leading-[1.7] max-w-xl mx-auto">
          Bu defterde anlattıklarımızın hepsi, her gün 8.200 öğünde uygulanıyor. Kurumunuzun
          yemek operasyonunu konuşmak için 48 saat içinde size dönelim.
        </p>
      </Reveal>

      <Reveal delay={500}>
        <div className="mt-11 flex flex-wrap items-center justify-center gap-7">
          <Magnetic strength={0.2}>
            <Link
              to="/iletisim"
              className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F5F3EF] px-8 py-[18px] text-[12px] tracking-[0.22em] uppercase font-medium rounded-sm transition-colors duration-300 hover:bg-[#9A7F4F]"
              data-testid="article-end-cta"
            >
              Teklif Al
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Magnetic>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            className="inline-flex items-center gap-2 text-[#1A1A1A] text-[12.5px] tracking-[0.18em] uppercase font-medium border-b border-[#1A1A1A]/30 pb-1.5 hover:border-[#9A7F4F] hover:text-[#9A7F4F] transition-colors"
          >
            {BRAND.phone}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

/* Related articles */
const Related = ({ slugs }) => {
  const items = ARTICLES.filter((a) => slugs.includes(a.slug)).slice(0, 3);
  if (items.length === 0) return null;
  return (
    <section className="bg-[#EFEBE3] py-24 lg:py-32">
      <div className="container-x">
        <div className="flex items-center gap-3 mb-12">
          <AnimatedDivider width="3rem" />
          <span
            className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Devamı Olabilir
          </span>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {items.map((a, i) => (
            <Reveal key={a.slug} delay={i * 110}>
              <Link to={`/lezzet-sirlari/${a.slug}`} className="group block">
                <div className="aspect-[4/3] overflow-hidden rounded-sm mb-5 bg-[#72272D]">
                  <img
                    src={a.cover}
                    alt={a.title}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"
                  />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className="text-[10px] tracking-[0.32em] uppercase text-[#9A7F4F] font-medium"
                    style={{ fontFamily: '"JetBrains Mono", monospace' }}
                  >
                    {rubricLabel(a.rubric)}
                  </span>
                </div>
                <h3
                  className="font-display text-[#1A1A1A] text-[20px] lg:text-[24px] leading-[1.22] tracking-[-0.005em] group-hover:text-[#9A7F4F] transition-colors"
                  style={{ fontWeight: 400, fontVariationSettings: '"opsz" 72, "SOFT" 30' }}
                >
                  {a.title}
                </h3>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const LezzetSirlariMakale = () => {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} — Lezzet Sırları · Tarhun Catering`;
    }
  }, [article]);

  if (!article) {
    return <Navigate to="/lezzet-sirlari" replace />;
  }

  // First para flag for drop cap
  let firstParaFound = false;

  return (
    <>
      <ReadingProgress />

      {/* HERO — full bleed cover */}
      <section className="relative h-[68vh] min-h-[520px] w-full overflow-hidden bg-[#0A0C0B] pt-[170px] lg:pt-[200px]">
        <div className="absolute inset-0">
          <img
            src={article.cover}
            alt={article.title}
            className="w-full h-full object-cover animate-[heroZoom_9s_ease-out_forwards]"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(10,12,11,0.55) 0%, rgba(10,12,11,0.22) 30%, rgba(10,12,11,0.10) 50%, rgba(10,12,11,0.55) 80%, rgba(10,12,11,0.92) 100%)',
            }}
          />
        </div>

        <div className="relative z-10 h-full flex items-end pb-16 lg:pb-24">
          <div className="container-x w-full">
            <div className="max-w-3xl">
              <Link
                to="/lezzet-sirlari"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase font-medium text-[#C9A875] hover:text-[#F5F0E5] transition-colors mb-7"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
                data-testid="back-to-archive"
              >
                <ArrowLeft size={13} />
                Tarhun Defteri
              </Link>

              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-[#C9A875]" />
                <span
                  className="text-[10.5px] tracking-[0.34em] uppercase text-[#C9A875] font-medium"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  {rubricLabel(article.rubric)}
                </span>
              </div>

              <h1
                className="font-display text-[#F5F0E5] leading-[1.02] tracking-[-0.018em]"
                style={{
                  fontSize: 'clamp(36px, 5.4vw, 76px)',
                  fontWeight: 400,
                  fontVariationSettings: '"opsz" 144, "SOFT" 30',
                  textShadow: '0 2px 24px rgba(0,0,0,0.40)',
                }}
              >
                {article.title}
              </h1>

              <p
                className="mt-7 lg:mt-8 leading-[1.6] max-w-xl"
                style={{
                  color: 'rgba(245,240,229,0.84)',
                  fontSize: 'clamp(15px, 1.4vw, 18px)',
                  textShadow: '0 1px 12px rgba(0,0,0,0.30)',
                }}
              >
                {article.subtitle}
              </p>

              <div
                className="mt-9 flex items-center gap-5 text-[10.5px] tracking-[0.28em] uppercase"
                style={{ color: 'rgba(245,240,229,0.72)', fontFamily: '"JetBrains Mono", monospace' }}
              >
                <span className="inline-flex items-center gap-2">
                  <Clock size={12} strokeWidth={1.6} className="text-[#C9A875]" /> {article.readTime}
                </span>
                <span className="h-px w-6 bg-[#C9A875]/55" />
                <span>{formatDate(article.publishedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BODY — single column */}
      <article className="bg-[#F5F3EF] py-20 lg:py-28">
        <div className="container-x max-w-[720px] mx-auto">
          {article.content.map((block, i) => {
            const isFirst = block.type === 'p' && !firstParaFound;
            if (isFirst) firstParaFound = true;
            return <Block key={i} block={block} isFirst={isFirst} />;
          })}

          {/* Author signature */}
          <div className="mt-20 pt-9 border-t border-[#9A7F4F]/30 flex items-center gap-4">
            <div
              className="h-12 w-12 rounded-full bg-[#72272D] grid place-items-center text-[#C9A875] text-[13px] font-medium tracking-wider border border-[#9A7F4F]/35"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              TS
            </div>
            <div>
              <div
                className="text-[10.5px] tracking-[0.30em] uppercase text-[#9A7F4F] font-medium mb-0.5"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                Yazan
              </div>
              <div className="text-[14px] text-[#1A1A1A] font-medium">Tarhun Şefi</div>
              <div className="text-[12px] text-[#1A1A1A]/60 mt-0.5 italic font-display">
                {rubricDesc(article.rubric)}
              </div>
            </div>
          </div>
        </div>
      </article>

      <EndCTA />

      {article.related && article.related.length > 0 && (
        <Related slugs={article.related} />
      )}
    </>
  );
};

export default LezzetSirlariMakale;
